import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { PageContainer } from '../components/PageContainer';
import { EventCard } from '../components/EventCard';
import type { Event, EventType } from '@wineryxplorer/shared';
import { EventFilters, EventsPagination, EventNameSearch } from '../components/Events';
import { useSearchScope } from '../hooks/useSearchScope';
import { RegionSearch } from '../components/SearchPage/RegionSearch';
import type { Region, CityLocation } from '../components/SearchPage/RegionSearch';
import {
  MapPanel,
  MobileMapButton,
  MobileMapOverlay,
  SearchAreaButton,
} from '../components/SearchPage';
import { SearchResultsWithMap } from '../components/SearchResultsWithMap';
import { SearchScopePill } from '../components/search/SearchScopePill';

// Shape the event.list venue field exposes (same on geo + non-geo path)
type EventVenue = {
  _id: string;
  name: string;
  address?: {
    city?: string;
    state?: string;
    coordinates?: {
      type?: string;
      coordinates?: [number, number]; // [lng, lat] GeoJSON
    };
  };
};

// Shape consumed by MapPanel (only the fields Events actually provides)
type MapVenue = {
  _id: string;
  name: string;
  venueType: string;
  address?: {
    city?: string;
    state?: string;
    coordinates?: { lat: number; lng: number } | null;
  };
};

export const Events = () => {
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [eventType, setEventType] = useState<EventType | undefined>(undefined);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [venueId, setVenueId] = useState<string>('');
  // Name search lives in the URL (`q` — not used by useSearchScope).
  const [searchParams, setSearchParams] = useSearchParams();
  const nameQuery = searchParams.get('q') ?? '';
  const debouncedNameQuery = useDebouncedValue(nameQuery.trim(), 300);
  const handleNameQueryChange = (next: string) => {
    const params = new URLSearchParams(searchParams);
    if (next) params.set('q', next);
    else params.delete('q');
    setSearchParams(params, { replace: true });
    setOffset(0);
  };
  // Mobile full-screen map, also in the URL (`map=1`) so Back closes it.
  const showMobileMap = searchParams.get('map') === '1';
  const setShowMobileMap = (show: boolean) => {
    const params = new URLSearchParams(searchParams);
    if (show) params.set('map', '1');
    else params.delete('map');
    setSearchParams(params);
  };
  const [highlightedVenueId, setHighlightedVenueId] = useState<string | null>(null);

  // Search scope (region / city / geolocation / map bounds)
  const {
    selectedRegion,
    selectedCity,
    mapBounds,
    pendingMapBounds,
    locationParam,
    boundsParam,
    hasUncommittedPan,
    scopePill,
    mapCenter,
    mapZoom,
    isLocating,
    geoError,
    handleRegionChange,
    handleCitySearch,
    handleUseMyLocation,
    handleMapBoundsChanged,
    handleCommitPendingBounds,
  } = useSearchScope();

  // Reset to offset 0 when scope changes. Mirrors page reset in SearchPage/Experiences.
  // userLocation is intentionally excluded — GPS floats vary per-read and would cause
  // spurious resets mid-browse. Offset is reset explicitly in onUseMyLocation instead.
  const prevScopeIdRef = useRef<string>(
    `${selectedRegion?.slug ?? ''}|${selectedCity?.name ?? ''}|${mapBounds ? `${mapBounds.ne.lat},${mapBounds.ne.lng},${mapBounds.sw.lat},${mapBounds.sw.lng}` : ''}`
  );
  useEffect(() => {
    const currentId = `${selectedRegion?.slug ?? ''}|${selectedCity?.name ?? ''}|${mapBounds ? `${mapBounds.ne.lat},${mapBounds.ne.lng},${mapBounds.sw.lat},${mapBounds.sw.lng}` : ''}`;
    if (prevScopeIdRef.current !== currentId) {
      prevScopeIdRef.current = currentId;
      setOffset(0);
    }
  }, [selectedRegion, selectedCity, mapBounds]);

  // Wraps handleUseMyLocation to reset pagination on explicit "Use my location" click
  const onUseMyLocation = useCallback(() => {
    handleUseMyLocation(() => {
      setOffset(0);
    });
  }, [handleUseMyLocation]);

  // Fetch venues for the filter dropdown
  const { data: venuesData } = trpc.venue.list.useQuery({
    limit: 100,
    offset: 0,
  });

  const { data, isLoading, error } = trpc.event.list.useQuery(
    {
      limit,
      offset,
      eventType,
      startDate: startDate.length > 0 ? startDate : undefined,
      endDate: endDate.length > 0 ? endDate : undefined,
      venueId: venueId.length > 0 ? venueId : undefined,
      location: locationParam,
      bounds: boundsParam,
      search: debouncedNameQuery || undefined,
    },
    // Keep the previous page on screen while a new search loads; otherwise the
    // isLoading early return below unmounts the search box mid-typing.
    { placeholderData: keepPreviousData }
  );

  // Derive deduped venue pins from event results
  const mapVenues = useMemo<MapVenue[]>(() => {
    if (!data) return [];
    const byVenueId = new Map<string, MapVenue>();
    for (const event of data.items) {
      const v = (event as { venue?: EventVenue | null }).venue;
      if (!v?._id || !v.name) continue;
      if (byVenueId.has(v._id)) continue;
      // GeoJSON Point: coordinates is [lng, lat]
      const geoCoords = v.address?.coordinates?.coordinates;
      const coordinates = Array.isArray(geoCoords)
        ? { lat: geoCoords[1], lng: geoCoords[0] }
        : null;
      byVenueId.set(v._id, {
        _id: v._id,
        name: v.name,
        venueType: 'winery',
        address: {
          city: v.address?.city,
          state: v.address?.state,
          coordinates,
        },
      });
    }
    return Array.from(byVenueId.values());
  }, [data]);

  // Loading and error render where the results go, so the search box stays usable
  // (an early return would leave the user unable to clear a failing search).
  const statusBlock = error ? (
    <div className="text-center py-8">
      <p className="text-red-600">Error loading events: {error.message}</p>
    </div>
  ) : isLoading || !data ? (
    <div className="text-center py-8">
      <p className="text-gray-600">Loading events...</p>
    </div>
  ) : null;

  const events = (data?.items ?? []) as Array<
    Event & {
      venue?: {
        name: string;
        address?: {
          city?: string;
          state?: string;
        };
      };
    }
  >;
  const total = data?.total ?? 0;

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const handlePreviousPage = () => {
    if (offset > 0) {
      setOffset(Math.max(0, offset - limit));
    }
  };

  const handleNextPage = () => {
    if (offset + limit < total) {
      setOffset(offset + limit);
    }
  };

  const handleEventTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setEventType(value === '' ? undefined : (value as EventType));
    setOffset(0); // Reset to first page when filter changes
  };

  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
    setOffset(0);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
    setOffset(0);
  };

  const handleVenueChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setVenueId(e.target.value);
    setOffset(0);
  };

  const clearDateRange = () => {
    setStartDate('');
    setEndDate('');
    setOffset(0);
  };

  const venues = venuesData?.items || [];

  // One MapPanel node shared by the desktop sidebar and the mobile overlay.
  const mapPanel = (
    <MapPanel
      venues={mapVenues}
      onBoundsChanged={handleMapBoundsChanged}
      highlightedVenueId={highlightedVenueId}
      onPinClick={setHighlightedVenueId}
      center={mapCenter}
      zoom={mapZoom}
      infoWindowLinkTo={(venueId) => `/events?venueId=${venueId}`}
      topCenterOverlay={
        hasUncommittedPan && pendingMapBounds ? (
          <SearchAreaButton
            bounds={pendingMapBounds}
            filters={{}}
            onCommit={handleCommitPendingBounds}
          />
        ) : null
      }
    />
  );

  return (
    <div className="px-0 md:px-4 pt-2 pb-4 max-w-6xl mx-auto">
      <PageContainer className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Events</h1>
          <p className="text-gray-600">Discover upcoming wine events and experiences</p>
        </div>

        <EventNameSearch value={nameQuery} onChange={handleNameQueryChange} />

        {/* Region / location search */}
        <div>
          <RegionSearch
            selectedRegion={selectedRegion}
            selectedCity={selectedCity}
            onRegionChange={(region: Region | null) => handleRegionChange(region)}
            onCitySearch={(city: CityLocation) => handleCitySearch(city)}
            onUseMyLocation={onUseMyLocation}
            isLocating={isLocating}
            geoError={geoError}
          />
        </div>

        {/* Active-scope pill */}
        {scopePill && (
          <div>
            <SearchScopePill label={scopePill.label} onDismiss={scopePill.onDismiss} />
          </div>
        )}

        {/* Filters */}
        <EventFilters
          eventType={eventType}
          venueId={venueId}
          startDate={startDate}
          endDate={endDate}
          venues={venues}
          onEventTypeChange={handleEventTypeChange}
          onVenueChange={handleVenueChange}
          onStartDateChange={handleStartDateChange}
          onEndDateChange={handleEndDateChange}
          onClearEventType={() => {
            setEventType(undefined);
            setOffset(0);
          }}
          onClearVenue={() => {
            setVenueId('');
            setOffset(0);
          }}
          onClearDateRange={clearDateRange}
        />

        {/* Events list + map (two-column on desktop when scope is set, list-only otherwise) */}
        {statusBlock ? (
          statusBlock
        ) : locationParam ? (
          <>
            <div className="flex justify-end lg:hidden">
              <MobileMapButton onClick={() => setShowMobileMap(true)} />
            </div>
            <SearchResultsWithMap map={mapPanel}>
              {events.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {events.map((event) => (
                    <EventCard key={event.id} event={event} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  No events found in this area. Try adjusting filters or panning the map.
                </div>
              )}
            </SearchResultsWithMap>
          </>
        ) : (
          <>
            {events.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {events.map((event) => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-600">No events found. Check back soon for new events!</p>
              </div>
            )}
          </>
        )}

        {/* Pagination Controls */}
        {!statusBlock && totalPages > 1 && (
          <EventsPagination
            offset={offset}
            limit={limit}
            total={total}
            currentPage={currentPage}
            totalPages={totalPages}
            onPreviousPage={handlePreviousPage}
            onNextPage={handleNextPage}
          />
        )}
      </PageContainer>

      {/* Mobile full-screen map overlay. Gated on scope, not results, so a
          "Search this area" that finds nothing doesn't yank the map away. */}
      {showMobileMap && locationParam && (
        <MobileMapOverlay map={mapPanel} onClose={() => setShowMobileMap(false)}>
          <div className="space-y-3">
            {events.map((event) => (
              <div
                key={event.id}
                className={`rounded-lg transition-shadow ${
                  highlightedVenueId &&
                  (event as { venue?: EventVenue | null }).venue?._id === highlightedVenueId
                    ? 'shadow-lg'
                    : ''
                }`}
              >
                <EventCard event={event} />
              </div>
            ))}
            {events.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4">
                {isLoading ? 'Searching...' : 'No events found in this area'}
              </p>
            )}
          </div>
        </MobileMapOverlay>
      )}
    </div>
  );
};
